<script setup lang="ts">
import BaseText from "@/library/BaseText.vue";
import BaseButton from "@/library/BaseButton.vue";
import BaseIcon from "@/library/BaseIcon.vue";
import { posthogCapture } from "@/scripts/posthog.js";
import { SUPPORT_EMAIL } from "@/scripts/constants";
import { tools } from "@/scripts/tools";
import { ref } from "vue";

defineProps<{
  documents: string[][];
}>();

const isEmailCopied = ref(false);

const emailDocuments = () => {
  posthogCapture(
    "dashboard_pay_wallet_kyc_flow_manual_review_send_documents_button_clicked"
  );
  window.location.href = "mailto:" + SUPPORT_EMAIL;
};

const copyEmail = async () => {
  if (isEmailCopied.value) return;

  try {
    await tools.copyToClipboard(SUPPORT_EMAIL);
    isEmailCopied.value = true;
    posthogCapture(
      "dashboard_pay_wallet_kyc_flow_manual_review_copy_email_button_clicked"
    );

    setTimeout(() => {
      isEmailCopied.value = false;
    }, 2000);
  } catch (error) {
    console.error("Failed to copy email:", error);
  }
};
</script>

<template>
  <div class="kyc-manual-review-docs">
    <header class="kyc-manual-review-docs__header">
      <BaseText
        class="kyc-manual-review-docs__title"
        as="h1"
        variant="headline-2-semibold"
      >
        We need a few more details to verify your identity.
      </BaseText>
      <p class="kyc-manual-review-docs__description">
        Please reach out to
        <u><a href="mailto:support@cloaked.com">support@cloaked.com</a></u>
        and be prepared to present one document from each section listed below.
        <b>
          Do not send any documents until you've received a response from our
          team.
        </b>
      </p>
    </header>

    <div class="kyc-manual-review-docs__documents-list">
      <div
        v-for="(documentList, index) in documents"
        :key="index"
        class="kyc-manual-review-docs__documents-item"
      >
        <div
          v-for="(document, docIndex) in documentList"
          :key="docIndex"
          class="kyc-manual-review-docs__documents-item-doc"
        >
          <div class="kyc-manual-review-docs__documents-item-doc-num">
            <BaseText
              class="kyc-manual-review-docs__documents-item-doc-num-text"
              as="p"
              variant="caption-semibold"
            >
              {{ docIndex + 1 }}
            </BaseText>
          </div>
          <BaseText
            class="kyc-manual-review-docs__documents-item-doc-name"
            as="p"
            variant="body-3-semibold"
          >
            {{ document }}
          </BaseText>
        </div>
      </div>
    </div>

    <footer class="kyc-manual-review-docs__footer">
      <BaseText
        class="kyc-manual-review-docs__footer-text"
        as="p"
        variant="body-3-regular"
      >
        Once you submit, we'll review them and get back to you within two
        business days. If you do not have the required documents, contact
        Cloaked Support for further assistance.
      </BaseText>
      <BaseButton
        variant="primary"
        icon="email"
        class="kyc-manual-review-docs__footer-button"
        @click="emailDocuments"
      >
        Email Documents
      </BaseButton>
      <div
        class="kyc-manual-review-docs__copy-section"
        @click="copyEmail"
      >
        <BaseText
          variant="body-3-semibold"
          class="kyc-manual-review-docs__copy-email"
          :title="isEmailCopied ? 'Copied!' : 'Copy support email'"
        >
          {{ SUPPORT_EMAIL }}
        </BaseText>
        <BaseIcon
          :name="isEmailCopied ? 'check-square' : 'copy'"
          class="kyc-manual-review-docs__copy-icon"
        />
      </div>
    </footer>
  </div>
</template>

<style scoped lang="scss">
/* stylelint-disable */
$component-name: "kyc-manual-review-docs";

.#{$component-name} {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &__header {
    max-width: 900px;
  }

  &__title {
    text-align: center;
  }

  &__description {
    text-align: center;
    margin-top: 12px;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
  }

  &__documents {
    &-list {
      margin-top: 36px;
      display: flex;
      flex-direction: row;
      gap: 20px;
      width: 100%;
      max-width: 900px;
    }

    &-item {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 12px;
      padding: 24px;
      border-radius: 30px;
      border: 1px solid $color-status-yield;
      background: rgba(250, 165, 66, 0.15);

      &-doc {
        display: flex;
        gap: 10px;

        &-num {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          min-width: 20px;
          min-height: 20px;
          border-radius: 50%;
          border: 1px solid $color-primary-100;

          &-text {
            line-height: 0;
          }
        }
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    margin-top: 36px;
    width: 100%;

    &-button {
      width: 100%;
      max-width: 305px;
    }
  }

  &__copy {
    &-section {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;

      &:hover {
        color: $color-primary-90;
      }
    }
  }
}
</style>
